import { useState, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { barterGroupApi, messageApi } from '@/services/api'
import { useParams, useNavigate } from 'react-router-dom'
import Card from '@/components/ui/Card'
import Button from '@/components/ui/Button'
import Badge from '@/components/ui/Badge'
import Loading from '@/components/ui/Loading'
import Alert from '@/components/ui/Alert'
import ImageUpload from '@/components/barters/ImageUpload'
import { useAuth } from '@/hooks/useAuth'
import { formatCurrency, formatDate, formatRelativeTime } from '@/utils/format'
import { ArrowLeft, Send, Package, User, Shield, ChevronDown, ChevronUp, Image as ImageIcon } from 'lucide-react'

// Separate component for barter images to manage its own state
function BarterImageSection({
  barterId,
  queryClient,
  barterUserId,
  currentUserId,
  currentUserType
}: {
  barterId: number,
  queryClient: any,
  barterUserId: number,
  currentUserId?: number,
  currentUserType?: string
}) {
  const { data: imagesData } = useQuery({
    queryKey: ['barter-images', barterId],
    queryFn: async () => {
      const token = localStorage.getItem('token')
      const response = await fetch(`/api/barter-images?barter_id=${barterId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })
      return response.json()
    },
  })

  // Check if user can edit this barter
  const canEdit = currentUserType === 'super_admin' || barterUserId === currentUserId

  return (
    <div className="border-t bg-gray-50 p-4">
      <ImageUpload
        barterId={barterId}
        images={imagesData?.data?.images || []}
        maxImages={25}
        canEdit={canEdit}
        onImagesChange={() => {
          queryClient.invalidateQueries({ queryKey: ['barter-images', barterId] })
          queryClient.invalidateQueries({ queryKey: ['barter', barterId] })
        }}
      />
    </div>
  )
}

export default function BarterGroupDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [expandedBarters, setExpandedBarters] = useState<Set<number>>(new Set())
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const isAdmin = user?.user_type === 'super_admin' || user?.user_type === 'admin'

  const toggleBarterImages = (barterId: number) => {
    setExpandedBarters(prev => {
      const newSet = new Set(prev)
      if (newSet.has(barterId)) {
        newSet.delete(barterId)
      } else {
        newSet.add(barterId)
      }
      return newSet
    })
  }

  const { data, isLoading } = useQuery({
    queryKey: ['barter-group', id],
    queryFn: () => barterGroupApi.get(Number(id)),
    refetchInterval: () => {
      // Only poll when window is focused to reduce unnecessary refreshes
      return document.hasFocus() ? 5000 : false
    },
  })

  const updateStatusMutation = useMutation({
    mutationFn: ({ groupId, status }: { groupId: number; status: string }) =>
      barterGroupApi.updateStatus(groupId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['barter-group', id] })
      queryClient.invalidateQueries({ queryKey: ['barter-groups'] })
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to update status')
    },
  })

  const sendMessageMutation = useMutation({
    mutationFn: (messageText: string) =>
      messageApi.sendGroup(Number(id), messageText),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['barter-group', id] })
      setMessage('')
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to send message')
    },
  })

  const group = data?.data?.group
  const members = group?.members || []
  const barters = group?.barters || []
  const messages = group?.messages || []

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault()
    if (!message.trim()) return

    sendMessageMutation.mutate(message)
  }

  if (isLoading) return <Loading />
  if (!group) return <div>Group not found</div>

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button variant="secondary" onClick={() => navigate('/dashboard/groups')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Barter Group #{group.id}</h1>
            <p className="text-gray-600">
              Created {formatDate(group.created_at)}
              {isAdmin && (
                <span className="ml-2 text-primary-600">
                  <Shield className="w-4 h-4 inline" /> Admin View
                </span>
              )}
            </p>
          </div>
        </div>
        <Badge status={group.status}>{group.status?.replace('_', ' ')}</Badge>
      </div>

      {error && <Alert type="error" message={error} onClose={() => setError('')} />}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Barters in Group */}
          <Card title="Barters in this Group">
            <div className="space-y-4">
              {barters.map((barter: any) => (
                <div key={barter.id} className="border rounded-lg">
                  <div
                    className="p-4 cursor-pointer hover:bg-gray-50"
                    onClick={() => navigate(`/dashboard/barters/${barter.id}`)}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <Package className="w-5 h-5 text-primary-600" />
                        <h4 className="font-semibold text-gray-900">{barter.title}</h4>
                      </div>
                      <Badge variant={barter.type === 'goods' ? 'primary' : 'success'}>
                        {barter.type}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                      {barter.description}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-gray-500">
                        <User className="w-4 h-4 inline mr-1" />
                        {barter.first_name} {barter.last_name}
                      </div>
                      <div className="text-lg font-semibold text-primary-600">
                        {formatCurrency(barter.dollar_value)}
                      </div>
                    </div>
                    <div className="mt-2">
                      <Badge status={barter.status}>{barter.status}</Badge>
                    </div>
                  </div>

                  {/* Image management toggle */}
                  <div className="border-t px-4 py-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        toggleBarterImages(barter.id)
                      }}
                      className="flex items-center space-x-2 text-sm text-primary-600 hover:text-primary-800"
                    >
                      <ImageIcon className="w-4 h-4" />
                      <span>Manage Images</span>
                      {expandedBarters.has(barter.id) ? (
                        <ChevronUp className="w-4 h-4" />
                      ) : (
                        <ChevronDown className="w-4 h-4" />
                      )}
                    </button>
                  </div>

                  {/* Image upload section */}
                  {expandedBarters.has(barter.id) && (
                    <BarterImageSection
                      barterId={barter.id}
                      barterUserId={barter.user_id}
                      currentUserId={user?.id}
                      currentUserType={user?.user_type}
                      queryClient={queryClient}
                    />
                  )}
                </div>
              ))}
            </div>
          </Card>

          {/* Group Members */}
          <Card title="Group Members">
            <div className="space-y-4">
              {members.map((member: any) => (
                <div
                  key={member.user_id}
                  className="flex items-start justify-between border-b pb-4 last:border-b-0"
                >
                  <div className="flex items-start space-x-3">
                    <div className="bg-primary-100 p-2 rounded-full">
                      <User className="w-5 h-5 text-primary-600" />
                    </div>
                    <div className="flex-1">
                      {member.user_id === user?.id ? (
                        <h4 className="font-semibold text-gray-900">
                          {member.user_name}{' '}
                          <span className="text-primary-600 text-sm">(You)</span>
                        </h4>
                      ) : (
                        <button
                          onClick={() => navigate(`/dashboard/users/${member.user_id}`)}
                          className="font-semibold text-primary-600 hover:text-primary-800 hover:underline"
                        >
                          {member.user_name}
                        </button>
                      )}
                      <p className="text-sm text-gray-500">{member.user_email}</p>

                      {member.barter_title && (
                        <div className="mt-2 bg-gray-50 p-3 rounded">
                          <div className="flex items-center space-x-2 mb-1">
                            <Package className="w-4 h-4 text-gray-600" />
                            <span className="font-medium text-sm">{member.barter_title}</span>
                          </div>
                          <p className="text-sm text-gray-600">{member.barter_description}</p>
                          <div className="flex items-center justify-between mt-2">
                            <Badge variant={member.barter_type === 'goods' ? 'primary' : 'success'}>
                              {member.barter_type}
                            </Badge>
                            <span className="text-sm font-semibold">
                              {formatCurrency(member.barter_dollar_value)}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <Badge status={member.status}>{member.status}</Badge>
                </div>
              ))}
            </div>
          </Card>

          {/* Group Chat */}
          <Card title="Group Chat">
            <div className="space-y-4">
              <div className="h-96 overflow-y-auto border rounded-lg p-4 bg-gray-50 space-y-3">
                {messages.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No messages yet. Start the conversation!</p>
                ) : (
                  messages.map((msg: any) => (
                    <div
                      key={msg.id}
                      className={`flex ${msg.user_id === user?.id ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                          msg.user_id === user?.id
                            ? 'bg-primary-600 text-white'
                            : 'bg-white text-gray-900 border'
                        }`}
                      >
                        <p className="text-xs opacity-75 mb-1">
                          {msg.user_id === user?.id ? (
                            'You'
                          ) : (
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                navigate(`/dashboard/users/${msg.user_id}`)
                              }}
                              className="hover:underline font-medium"
                            >
                              {msg.user_name || `${msg.first_name} ${msg.last_name}`}
                            </button>
                          )}{' '}
                          • {formatRelativeTime(msg.created_at)}
                        </p>
                        <p className="text-sm">{msg.message}</p>
                      </div>
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>

              <form onSubmit={handleSendMessage} className="flex space-x-2">
                <input
                  type="text"
                  className="input flex-1"
                  placeholder="Type your message..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  disabled={sendMessageMutation.isPending}
                />
                <Button type="submit" disabled={!message.trim()} isLoading={sendMessageMutation.isPending}>
                  <Send className="w-4 h-4" />
                </Button>
              </form>
            </div>
          </Card>
        </div>

        {/* Actions Sidebar */}
        <div className="space-y-6">
          <Card title="Group Actions">
            <div className="space-y-3">
              {group.status === 'pending' && (
                <>
                  <Button
                    variant="primary"
                    className="w-full"
                    onClick={() =>
                      updateStatusMutation.mutate({ groupId: Number(id), status: 'active' })
                    }
                    isLoading={updateStatusMutation.isPending}
                  >
                    Activate Group
                  </Button>
                  <Button
                    variant="danger"
                    className="w-full"
                    onClick={() =>
                      updateStatusMutation.mutate({ groupId: Number(id), status: 'cancelled' })
                    }
                    isLoading={updateStatusMutation.isPending}
                  >
                    Cancel Group
                  </Button>
                </>
              )}

              {group.status === 'active' && (
                <Button
                  variant="primary"
                  className="w-full"
                  onClick={() =>
                    updateStatusMutation.mutate({ groupId: Number(id), status: 'completed' })
                  }
                  isLoading={updateStatusMutation.isPending}
                >
                  Mark as Completed
                </Button>
              )}

              {group.status === 'completed' && (
                <div className="text-center py-4">
                  <p className="text-green-600 font-semibold">✓ Barter Completed</p>
                  <Button
                    variant="secondary"
                    className="w-full mt-3"
                    onClick={() => navigate('/dashboard/reviews')}
                  >
                    Leave Reviews
                  </Button>
                </div>
              )}
            </div>
          </Card>

          <Card title="Group Statistics">
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Total Members:</span>
                <span className="font-semibold">{members.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Messages:</span>
                <span className="font-semibold">{messages.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Barters:</span>
                <span className="font-semibold">{barters.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Total Value:</span>
                <span className="font-semibold">
                  {formatCurrency(
                    barters.reduce((sum: number, b: any) => sum + (parseFloat(b.dollar_value) || 0), 0)
                  )}
                </span>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  )
}
